import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { hasNonEmptyValue } from 'Lib/Any';
import { cookieExists, setCookie } from '../../../Lib/cookies';
import { AuthenticationState, GetSettingsResolveAction, PostLoginAction } from './types';

const authenticatedCookieName = 'SQ.Authenticated';
const name = 'authentication';

const initialState: AuthenticationState = {
  authChecked: false,
  isAuthenticated: false,
  isAuthenticating: false,
  statusMessage: undefined,
  platformVersion: '',
  mustChangeMessage: null,
  authSettings: undefined,
  entra: {
    isAuthenticating: false,
    isInitialising: false,
    isInitialised: false,
    autoLogin: true,
  },
  isRetrievingSettings: false,
};

const slice = createSlice({
  name,
  initialState,
  reducers: {
    postLogin: (state, action: PostLoginAction) => {
      state.isAuthenticating = true;
      state.statusMessage = { message: 'Logging in...', severity: 'info', border: false };
    },
    postLoginResolve: (state) => {
      state.isAuthenticated = true;
      state.isAuthenticating = false;
      state.statusMessage = undefined;
      state.mustChangeMessage = null;
    },
    postLoginReject: (state, { payload: error }: PayloadAction<any>) => {
      state.isAuthenticated = false;
      state.isAuthenticating = false;
      const errorMessage =
        error.additionalInformation.authLookupResult === 'Misconfiguration'
          ? error.additionalInformation.authLookupResultMessage
          : error.message;
      state.statusMessage = { message: errorMessage, severity: 'error' };
      state.mustChangeMessage =
        error.additionalInformation.authLookupResult === 'MustChangePassword'
          ? error.additionalInformation.authLookupResultMessage
          : null;
    },
    postLogout: (state) => {
      setCookie(authenticatedCookieName, '0');
      state.isAuthenticated = false;
      state.isAuthenticating = false;
      state.statusMessage = undefined;
      state.mustChangeMessage = null;
      state.entra.autoLogin = false;
    },
    clearChangePassword: (state) => {
      setCookie(authenticatedCookieName, '0');
      state.isAuthenticated = false;
      state.isAuthenticating = false;
      state.statusMessage = undefined;
      state.mustChangeMessage = null;
    },
    getPlatformVersion: (state) => {},
    getPlatformVersionResolve: (state, { payload: platformVersion }: PayloadAction<string>) => {
      state.platformVersion = platformVersion;
    },
    getPlatformVersionReject: (state) => {
      state.platformVersion = 'Unknown';
    },
    requestFailure: (state) => {},
    checkLogin: (state) => {
      state.isAuthenticated = cookieExists(authenticatedCookieName, '1');
      state.authChecked = true;
      state.statusMessage = undefined;
    },
    getSettings: (state) => {
      state.isRetrievingSettings = true;
    },
    getSettingsResolve: (state, action: GetSettingsResolveAction) => {
      state.authSettings = action.payload;
      state.isRetrievingSettings = false;
    },
    getSettingsReject: (state) => {
      state.authSettings = undefined;
      state.isRetrievingSettings = false;
    },
    initialiseEntra: (state) => {
      state.entra.isInitialising = true;
      state.entra.isInitialised = false;
    },
    initialiseEntraResolve: (state) => {
      state.entra.isInitialising = false;
      state.entra.isInitialised = true;
    },
    initialiseEntraReject: (state, { payload: error }: PayloadAction<any>) => {
      state.entra.isInitialising = false;
      state.entra.isInitialised = true;
      if (hasNonEmptyValue(error.message)) state.statusMessage = { message: error.message, severity: error.severity };
    },
    postEntraLogin: (state) => {
      state.entra.isAuthenticating = true;
    },
    postEntraLoginResolve: (state) => {
      state.entra.isAuthenticating = false;
    },
    postEntraLoginReject: (state, { payload: error }: PayloadAction<any>) => {
      state.entra.isAuthenticating = false;
      state.statusMessage = { message: error.message, severity: error.severity };
    },
    postEntraLogout: (state) => {
      state.entra.autoLogin = false;
    },
  },
});

const { actions, reducer } = slice;
export { actions, reducer };
